import { ChevronDown, ChevronRight, Info, TriangleAlert } from "lucide-react"

import type { Elemento } from "./tipos"

/**
 * Un elemento de la pantalla, replicado: se ve como en el sistema, pero no se pulsa.
 *
 * ⚠ **No son controles de verdad.** Un botón que parece botón y no hace nada confunde, y
 * en una guía confunde dos veces: por eso son `span` con la apariencia de su clase y el
 * lector de pantalla los oye como texto («botón: Guardar»).
 */
export function Replica({ e }: { e: Elemento }) {
  switch (e.tipo) {
    case "ruta":
      return (
        <span className="inline-flex flex-wrap items-center gap-1 text-xs">
          {e.pasos.map((p, i) => (
            <span key={p} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} aria-hidden={true} />}
              <span className="activo-cristal rounded-[var(--radius-control)] px-2 py-0.5">
                {p}
              </span>
            </span>
          ))}
        </span>
      )
    case "pestanas":
      return (
        <span className="inline-flex flex-wrap gap-1 text-xs">
          {e.opciones.map((o) => (
            <span
              key={o}
              className={`rounded-[var(--radius-control)] px-2 py-0.5 ${o === e.activa ? "activo-cristal font-semibold" : "toque-cristal"}`}
            >
              {o}
            </span>
          ))}
        </span>
      )
    case "campo":
      return (
        <span
          className={`block ${e.ancho === "corto" ? "max-w-40" : e.ancho === "largo" ? "max-w-md" : "max-w-xs"}`}
        >
          <span className="mb-0.5 block text-xs" style={{ color: "var(--fg-muted)" }}>
            {e.etiqueta}
          </span>
          <span className="campo block truncate">{e.valor}</span>
        </span>
      )
    case "lista":
      return (
        <span className="block max-w-xs">
          <span className="mb-0.5 block text-xs" style={{ color: "var(--fg-muted)" }}>
            {e.etiqueta}
          </span>
          <span className="campo flex items-center justify-between gap-2">
            <span className="truncate">{e.valor}</span>
            <ChevronDown size={14} aria-hidden={true} />
          </span>
        </span>
      )
    case "boton":
      return (
        <span
          className={`${e.primario ? "boton-primario" : "boton-secundario"} inline-flex h-8 items-center px-3 text-xs`}
        >
          <span className="sr-only">botón: </span>
          {e.texto}
        </span>
      )
    case "nota":
      return (
        <span
          className="flex items-start gap-2 rounded-[var(--radius-control)] p-2 text-xs"
          style={{
            background: "var(--cristal-relleno)",
            color: e.tono === "aviso" ? "var(--warning)" : undefined,
          }}
        >
          {e.tono === "aviso" ? (
            <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden={true} />
          ) : (
            <Info size={14} className="mt-0.5 shrink-0" aria-hidden={true} />
          )}
          <span>{e.texto}</span>
        </span>
      )
  }
}
